"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchDerivedEVMAddress = exports.fetchEVMFeeProperties = void 0;
const ethers_1 = require("ethers");
const kdf_1 = require("../../kdf/kdf");
const signature_1 = require("../../signature");
const utils_1 = require("../../kdf/utils");
/**
 * Estimates the amount of gas that a transaction will consume.
 *
 * This function calls the underlying JSON RPC's `estimateGas` method to
 * predict how much gas the transaction will use. This is useful for setting
 * gas limits when sending a transaction to ensure it does not run out of gas.
 *
 * @param {string} providerUrl - The providerUrl of the EVM network to query the fee properties from.
 * @param {ethers.TransactionLike} transaction - The transaction object for which to estimate gas. This function only requires the `to`, `value`, and `data` fields of the transaction object.
 * @returns {Promise<bigint>} A promise that resolves to the estimated gas amount as a bigint.
 */
function fetchEVMFeeProperties(providerUrl, transaction) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const provider = new ethers_1.ethers.JsonRpcProvider(providerUrl);
        const gasLimit = yield provider.estimateGas(transaction);
        const feeData = yield provider.getFeeData();
        const maxFeePerGas = (_a = feeData.maxFeePerGas) !== null && _a !== void 0 ? _a : ethers_1.ethers.parseUnits('10', 'gwei');
        const maxPriorityFeePerGas = (_b = feeData.maxPriorityFeePerGas) !== null && _b !== void 0 ? _b : ethers_1.ethers.parseUnits('10', 'gwei');
        return {
            gasLimit,
            maxFeePerGas,
            maxPriorityFeePerGas,
            maxFee: maxFeePerGas * gasLimit,
        };
    });
}
exports.fetchEVMFeeProperties = fetchEVMFeeProperties;
/**
 * Derives an Ethereum address for a given signer ID and derivation path.
 *
 * This method leverages the root public key associated with the signer ID to generate an Ethereum address
 * and public key based on the specified derivation path.
 *
 * @param {string} signerId - The identifier of the signer.
 * @param {KeyDerivationPath} path - The derivation path used for generating the address.
 * @param {string} nearNetworkId - The near network id used to interact with the NEAR blockchain.
 * @param {ChainSignatureContracts} multichainContractId - The contract identifier used to get the root public key.
 * @returns {Promise<string>} A promise that resolves to the derived Ethereum address.
 */
function fetchDerivedEVMAddress(_a) {
    return __awaiter(this, arguments, void 0, function* ({ signerId, path, nearNetworkId, multichainContractId, }) {
        const contractRootPublicKey = yield (0, signature_1.getRootPublicKey)(multichainContractId, nearNetworkId);
        if (!contractRootPublicKey) {
            throw new Error('Failed to fetch root public key');
        }
        return yield (0, kdf_1.generateEthereumAddress)(signerId, (0, utils_1.getCanonicalizedDerivationPath)(path), contractRootPublicKey);
    });
}
exports.fetchDerivedEVMAddress = fetchDerivedEVMAddress;
